/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import type { AxiosError, AxiosResponse } from 'axios';

import { doesRestApiExist } from '../rest-client';
import { resolveRestApi } from '../router/app-router';
import { fetch } from '../service_broker/fetch';
import EnvironmentInfo from '../environment-info';
import constants from '../constants';

import type { CacheOptions, ComponentState } from './types';
import PubSubUtils from './pubsub-utils';
import Utils from './utils';

/**
 * @description
 * Exposes APIs to maintain the state of UI components
 */
class NavigationUtils {
  public userSettings: any;
  componentCache: { [key: string]: ComponentState };
  contextMapToClearOnCancel: { [context: string]: Set<string> };
  #isClearOnCancelRegistered: boolean;

  constructor() {
    this.userSettings = {};
    this.componentCache = {};
    this.contextMapToClearOnCancel = {};
    this.#isClearOnCancelRegistered = false;
  }

  /**
   * This function returns the normalized form for the key
   * @private
   * @param key string to normalize
   * @returns normalized string
   */
  getNormalizedKey(key: string) {
    return key?.toString().trim().replace(/\s/g, '-').toLowerCase();
  }

  /**
   * This API updates the state of a specified UI component. The updated state is stored in a browser session.
   * @description This will save the state in browser session
   * @param key -- The ID of the UI component whose state must be updated
   * @param state -- The data that must be set as the state of the UI component
   * @function
   * @example <caption>In this example, the API updates the state of a UI component whose key is on8ttl-c11n-work-d-2001-caseview.</caption>
   * PCore.getNavigationUtils().setComponentState(key, object)
   * PCore.getNavigationUtils().setComponentState("on8ttl-c11n-work-d-2001-caseview", {
   *  active: 0
   * });
   */
  setComponentState = (key: string, state: any) => {
    if (!sessionStorage) {
      console.error('Session Storage not found');
      return;
    }
    sessionStorage.setItem(this.getNormalizedKey(key), JSON.stringify(state));
  };

  /**
   * This API obtains the state of a specified UI component
   * @description This API returns the state of the UI component as a Java Script object
   * @param key -- The ID of the UI component whose state must be retrieved
   * @returns -- the saved state with the corresponding key <br> Eg : {active:0}
   * @function
   * @example <caption>In this example, the API obtains the state of the UI component whose key is on8ttl-c11n-work-d-2001-caseview</caption>
   * PCore.getNavigationUtils().getComponentState(key)
   * PCore.getNavigationUtils().getComponentState("on8ttl-c11n-work-d-2001-caseview");
   */
  getComponentState = (key: string) => {
    const storedData = sessionStorage.getItem(this.getNormalizedKey(key));
    const parsedData = storedData ? Utils.isValidJson(storedData) : undefined;
    if (!parsedData) return storedData;
    return parsedData;
  };

  /**
   * This API remove the state of a specified UI component
   * @param key -- The ID of the UI component whose state must be removed
   * @function
   * @example <caption>In this example, the API remove the state of the UI component whose key is on8ttl-c11n-work-d-2001-caseview</caption>
   * PCore.getNavigationUtils().removeComponentState(key)
   * PCore.getNavigationUtils().removeComponentState("on8ttl-c11n-work-d-2001-caseview")
   */

  removeComponentState = (key: string) => {
    sessionStorage.removeItem(this.getNormalizedKey(key));
  };

  /**
   * This API makes get request to obtain the saved state of the userSettings
   * @private
   * @function
   * @example <caption>In this example, the API obtains saved state of the userSettings and saves them in class reference</caption>
   * PCore.getNavigationUtils().fetchUserSettings()
   */
  fetchUserSettings = async () => {
    if (doesRestApiExist('getUserSettings')) {
      const { restApi, reqType } = resolveRestApi('getUserSettings');
      fetch(restApi, {
        method: reqType,
        headers: {}
      })
        .then((res: any) => {
          if (res.status === 200 && res.data) {
            this.userSettings = res.data;
          } else {
            throw new Error('something went wrong while fetching getUserSettings');
          }
        })
        .catch((err: any) => console.error(err));
    }
  };

  /**
   * Initializes the userSettings attribute of the NavigationUtils class.
   * @function
   * @example <caption>In this example, the API initializes the userSettings attribute of the NavigationUtils class.</caption>
   * PCore.getNavigationUtils().init();
   */
  init = () => {
    // Remove the else condition when relevent infinity changes are present. it should only happen through env info
    if (EnvironmentInfo.getUserSettings()) {
      this.userSettings = EnvironmentInfo.getUserSettings();
    } else {
      this.fetchUserSettings();
    }
  };

  /**
   * This Api obtains the value of a specified property in the userSettings attribute.
   * @param path -- The location of the property whose value must be obtained.
   * @returns -- The value of the property from userSettings attribute <br> Eg : {Case:{summaryPanelExpanded:true}}
   * @function
   * @example <caption>In this example, the API returns the value of the 'prop1' property in the userSettings attribute.</caption>
   * PCore.getNavigationUtils().getUserSettings('prop1');
   */
  getUserSettings = (path: string) => {
    if (!path) {
      throw new Error('path is required parameter');
    }

    if (typeof path !== 'string') {
      throw new TypeError('path must be string');
    }
    return Utils.getIn(this.userSettings, path, {});
  };

  /**
   * This API specifies or updates values of properties in the userSettings attribute.
   * @param path -- The path of the UI component whose state must be updated
   * @param value -- The value that must be assigned to the property.
   * @returns -- A promise that resolves to the updated user settings as an object <br> Eg : new Promise().then((res) => console.log(res)})
   * @function
   * @example <caption>In this example, the API sets the value of the 'prop1' property to true in the userSettings attribute.</caption>
   * PCore.getNavigationUtils().setUserSettings('prop1', true);
   */
  setUserSettings = async (path: string, value: string) => {
    if (!path) {
      return Promise.reject(new Error('path is required parameter'));
    }

    if (typeof path !== 'string') {
      return Promise.reject(new TypeError('path must be string'));
    }

    if (value === undefined || value === null || value === '') {
      return Promise.reject(new Error('value is required parameter'));
    }
    let restAPIInfo;
    if (doesRestApiExist('updateUserSettings')) {
      restAPIInfo = resolveRestApi('updateUserSettings');
    } else {
      return Promise.resolve('updateUserSettings route is not available');
    }
    const { restApi, reqType } = restAPIInfo;
    return fetch(restApi, {
      method: reqType,
      body: Utils.setIn({}, path, value)
    })
      .then((res: AxiosResponse) => {
        if (res.status === 200 && res.data) {
          this.userSettings = res.data;
        } else {
          throw new Error('something went wrong');
        }
      })
      .catch((err: AxiosError) => {
        console.error(err);
      });
  };

  /**
   * This API updates the state of a specified key. The updated state is stored in a cache.
   * @param key - The key whose state must be updated. The key should be unique across the application.
   * @param value - The data that must be set as the value for the specified key.
   * @param options - optional arguments
   * options.clearOnCancelForContext - context for which the key is associated.
   * @function
   * @example <caption> In this example, the API updates the value whose key is  uniquekeyacrosstheapp' .</caption>
   * PCore.getNavigationUtils().setComponentState(key, object)
   * PCore.getNavigationUtils().setComponentState("uniquekeyacrosstheapp", {
   *  search: {
   *    filter1: 'name'
   *  },
   *  {
   *    clearOnCancelForContext: 'app/primary_1/workarea_1
   *  }
   * });
   */
  setComponentCache(key: string, value: ComponentState, options: CacheOptions = {}): void {
    if (typeof key !== 'string' || !key?.trim()) {
      return;
    }
    const context = options.clearOnCancelForContext;
    if (context) {
      if (Object.hasOwn(this.contextMapToClearOnCancel, context)) {
        this.contextMapToClearOnCancel[context].add(this.getNormalizedKey(key));
      } else {
        this.contextMapToClearOnCancel[context] = new Set([this.getNormalizedKey(key)]);
      }
    }
    this.componentCache[this.getNormalizedKey(key)] = value;
    if (!this.#isClearOnCancelRegistered) {
      this.#isClearOnCancelRegistered = true;
      const resetContextFromCache = (data: { caseID: string; assignMentID: string; context: string }) => {
        const { context: contextName } = data || {};
        const keys = this.contextMapToClearOnCancel[contextName] || new Set();
        keys.forEach((val) => {
          this.resetComponentCache(val);
        });
        delete this.contextMapToClearOnCancel[contextName];
      };
      PubSubUtils.subscribe(constants.PUB_SUB_EVENTS.EVENT_CANCEL, resetContextFromCache, 'RESET_COMPONENT_CACHE');
    }
  }

  /**
   * This API returns the value of a specified key.
   * @param key - The key whose value must be returned.
   * @returns - The value which associated with the key. ComponentState can be string | number | boolean | object | null
   */
  getComponentCache(key: string): ComponentState | void {
    if (typeof key !== 'string' || !key?.trim()) {
      return null;
    }
    if (Object.hasOwn(this.componentCache, this.getNormalizedKey(key))) {
      return this.componentCache[this.getNormalizedKey(key)];
    }
    return undefined;
  }

  /**
   * This API returns the value value of the query param in the URL.
   * @returns - The the query params of the page URL.
   */
  getURLParam(key: string, decode: boolean): string | null {
    const url = new URL(window.location.href);
    let value = url.searchParams.get(key);
    if (!value) {
      return null;
    }
    value = decode ? atob(value) : value;
    return value;
  }

  /**
   * This API sets the specified query param in the page URL.
   * @param key - The key whose state must be updated. The key should be unique across the application.
   * @param value - The data that must be set as the value for the specified key.
   * @param encode - (optional) If true, the value will be encoded before setting the parameter, otherwise it will be set as is.
   * @function
   * @example <caption> In this example, the API sets the query param whose key is  activeTab' .</caption>
   * PCore.getNavigationUtils().setURLParam(key, object)
   * PCore.getNavigationUtils().setURLParam("activeTab", "Epics");
   */
  setURLParam(key: string, value: string, encode?: boolean): void {
    if (!EnvironmentInfo.shouldUseURLParams()) {
      return;
    }
    const url = new URL(window.location.href);
    value = encode ? btoa(value) : value;
    if (url.searchParams.get(key) === value) {
      return;
    }
    url.searchParams.set(key, value);
    const state = {
      type: 'ui-navigation'
    };
    window.history.pushState(state, '', url);
  }

  /**
   * This API removes the value of the corresponding key from the cache.
   * @param key The key value state must be removed.
   * @returns - it returns true if the key is removed from the cache successfully.
   */
  resetComponentCache(key: string): boolean {
    if (typeof key !== 'string' || !key?.trim()) {
      return false;
    }
    if (Object.hasOwn(this.componentCache, this.getNormalizedKey(key))) {
      delete this.componentCache[this.getNormalizedKey(key)];
      return true;
    }
    return false;
  }
}

export default new NavigationUtils();
